/**
 * ★ TEMPORARY DIAGNOSTIC — delete once the iOS post-ad resume bug is fixed.
 *
 * WebKit cannot run under WSL and Chromium disagrees with a real iPhone about what IMA does after a
 * pre-roll, so the only way to see the truth is to record it *on the device*. Safari's Web Inspector
 * makes pasting a recorder into a nested frame painful, so the recorder ships — inert.
 *
 * OFF unless the container URL carries `?adtrace=1`. Nothing is recorded, nothing is rendered, and
 * the only cost on a normal load is one `URLSearchParams` read.
 *
 *     https://app.yoreflix.uk/?adtrace=1
 *
 * The log lives on `window.__adTrace` inside the `viewershaka` frame. The host reads it directly
 * (same origin) and paints it over the page, because when playback dies you are returned to the
 * detail page and the shaka frame is hidden.
 */

export interface AdTraceEntry {
  /** Milliseconds since the recorder started — relative, so the numbers stay readable. */
  ms: number
  /** A bridge message type (`AdBreakStart`), or a media event (`video:emptied`). */
  t: string
  /** Bridge payload, or the video element's state at that instant. */
  d?: unknown
}

declare global {
  interface Window {
    __adTrace?: AdTraceEntry[]
  }
}

/** `?adtrace=1` on the TOP document. Same-origin, so a guest frame can read it; never throw if not. */
export function isAdTraceEnabled(): boolean {
  try {
    const search = window.top?.location.search ?? window.location.search
    return new URLSearchParams(search).get('adtrace') === '1'
  } catch {
    return false
  }
}

/**
 * Start recording, in whichever frame calls this. Records every bridge message the player sends and
 * every media event on the content `<video>` — which is precisely the pair that disagrees between
 * Chromium and WebKit: on the mobile path IMA is handed the content element, and what it leaves
 * behind afterwards is the whole question.
 */
export function installAdTrace(video: HTMLVideoElement, bridge: { send: (t: string, p?: unknown) => void }): void {
  const started = performance.now()
  const log: AdTraceEntry[] = []
  window.__adTrace = log
  const at = () => Math.round(performance.now() - started)

  const original = bridge.send.bind(bridge)
  bridge.send = (t: string, p?: unknown) => {
    log.push({ ms: at(), t, d: p })
    original(t, p)
  }

  const snapshot = () => ({
    src: (video.currentSrc || video.src || '(empty)').slice(0, 48),
    t: +video.currentTime.toFixed(2),
    paused: video.paused,
    ready: video.readyState,
    err: video.error?.code ?? null,
    videos: document.querySelectorAll('video').length,
  })

  // `emptied` / `loadstart` / `abort` are the tell: they fire when something takes the element away
  // from Shaka's MediaSource. `error` + `stalled` say it died rather than was replaced.
  for (const ev of ['play', 'pause', 'ended', 'emptied', 'loadstart', 'abort', 'error', 'stalled', 'waiting']) {
    video.addEventListener(ev, () => log.push({ ms: at(), t: `video:${ev}`, d: snapshot() }))
  }

  log.push({ ms: 0, t: 'trace:started', d: { ua: navigator.userAgent.slice(0, 80), ...snapshot() } })
}
